/** PageSingle component - render a basic static Page from Contentful */
import { GetStaticProps, GetStaticPaths } from 'next'
import React from 'react'

import PageComponent from '@aletheia/common/components/Page'
import { Override } from '@aletheia/common/utils/types'

import Markdown from '../components/Markdown'
import { IPage, fetchPage, fetchPages, isLink, isEntry } from '../contentful'

function getPageParents(Page: IPage): IPage[] {
  const pages: IPage[] = []
  if (Page.fields.parent && isEntry(Page.fields.parent)) {
    pages.unshift(...getPageParents(Page.fields.parent))
  }
  pages.push(Page)
  return pages
}

function getPageBreadcrumbs(page: IPage) {
  const pages = getPageParents(page)
  return pages.map((Page, index) => ({
    href: `/${pages
      .slice(0, index + 1)
      .map((Page) => Page.fields.slug)
      .join('/')}`,
    label: Page.fields.shortTitle || Page.fields.title,
  }))
}

type StaticPageProps = {
  Page: IPage
}

const PageSingle: React.FC<StaticPageProps> = ({ Page }) => {
  const { title, body, bannerImage } = Page.fields

  const breadcrumbs = getPageBreadcrumbs(Page)

  return (
    <PageComponent
      title={title}
      banner={bannerImage}
      breadcrumbs={breadcrumbs.length > 1 ? breadcrumbs : undefined}
    >
      <Markdown>{body}</Markdown>
    </PageComponent>
  )
}

export default PageSingle

export const getStaticProps: GetStaticProps<
  StaticPageProps,
  { path: string }
> = async ({ params }) => {
  if (!params?.path) throw new Error('Missing path')
  const Page = await fetchPage(params.path)
  if (!Page) {
    return { notFound: true }
  }
  return {
    props: { Page },
    revalidate: 20,
  }
}

/** Type to use with the guard clause below */
type PageWithSlug = Override<
  IPage,
  {
    fields: Override<IPage['fields'], { slug: string }>
  }
>

/** Guard to check that a Page has a valid slug */
function isPageWithSlug(Page: IPage): Page is PageWithSlug {
  return !!Page.fields.slug && typeof Page.fields.slug === 'string'
}

const EXCLUDED_PAGES = [
  '404',
  'team',
  'donor-lottery',
  'donor-lottery-in-detail',
  'donation-advice',
]

export const getStaticPaths: GetStaticPaths = async () => {
  const Pages = await fetchPages()
  const paths = Pages.filter(isPageWithSlug)
    .filter((page) => !EXCLUDED_PAGES.includes(page.fields.slug))
    .map((Page) => ({
      params: { path: Page.fields.slug },
    }))

  return {
    paths,
    fallback: 'blocking',
  }
}
